import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateSetWeightRecommendationOverride } from 'redux/slices/activeWorkoutV5'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'

export default function LoadCompletedV5({ 
    kinesisWorkoutAssignmentType,
    groupIndividual = null,
    phaseIndex, 
    slotIndex, 
    exercise,
    setIndex, 
    set, 
    isCompleted, 
    isSkipped,
    showLabel = true
}) {
    const { urlWorkoutId } = useParams()

    const dispatch = useDispatch()

    const disableCardInputs = isCompleted === 1 || isSkipped === 1

    var groupIndividualId = null
    var groupIndividualSet = null
    if (kinesisWorkoutAssignmentType === 'group-individual') {
        groupIndividualId = groupIndividual?.id
        groupIndividualSet = groupIndividual?.workout?.phases[phaseIndex]?.slots[slotIndex]?.sets[setIndex]
    }

    var weightRecommendationLoaded = false
    if (kinesisWorkoutAssignmentType === 'group') weightRecommendationLoaded = set.weightRecommendationLoaded
    if (kinesisWorkoutAssignmentType === 'group-individual') weightRecommendationLoaded = groupIndividualSet?.weightRecommendationLoaded
    if (kinesisWorkoutAssignmentType === 'individual') weightRecommendationLoaded = set.weightRecommendationLoaded

    var weightRecommendationRounded = 0
    if (kinesisWorkoutAssignmentType === 'group') weightRecommendationRounded = set.weightRecommendationRounded || 0
    if (kinesisWorkoutAssignmentType === 'group-individual') weightRecommendationRounded = groupIndividualSet?.weightRecommendationRounded || 0
    if (kinesisWorkoutAssignmentType === 'individual') weightRecommendationRounded = set.weightRecommendationRounded || 0

    var labelWeightRecommendationRounded = 0
    if (kinesisWorkoutAssignmentType === 'group') labelWeightRecommendationRounded = set.labelWeightRecommendationRounded || 0
    if (kinesisWorkoutAssignmentType === 'group-individual') labelWeightRecommendationRounded = groupIndividualSet?.labelWeightRecommendationRounded || 0
    if (kinesisWorkoutAssignmentType === 'individual') labelWeightRecommendationRounded = set.labelWeightRecommendationRounded || 0

    const [isScaling, setIsScaling] = useState(false)
    const [isInputBoxCurrentlyFocused, setIsInputBoxCurrentlyFocused] = useState(false)
    const [inputBoxValue, setInputBoxValue] = useState(weightRecommendationRounded)

    // Increment/decrement functions

    const DecrementLoadCompleted = () => {

        const newWeight = Math.max(0, weightRecommendationRounded - 5)

        __DispatchWeightRecommendationOverride(
            newWeight,
            newWeight,
            newWeight - (weightRecommendationRounded || 0)
        )

        TriggerScaleAnimation()
    }

    const IncrementLoadCompleted = () => {

        const newWeight = weightRecommendationRounded + 5

        __DispatchWeightRecommendationOverride(
            newWeight,
            newWeight,
            newWeight - (weightRecommendationRounded || 0)
        )

        TriggerScaleAnimation()
    }

    const TriggerScaleAnimation = () => {
        setIsScaling(true)
        setTimeout(() => setIsScaling(false), 200)
    }

    // Input box functions

    const HandleInputBoxFocus = () => {
        if (disableCardInputs) return

        setIsInputBoxCurrentlyFocused(true)
        setInputBoxValue('')
    }

    const HandleLoadCompletedChange = (value) => {
        if (disableCardInputs) return

        // Allow empty string
        if (value === '') {
            setInputBoxValue('')
            return
        }

        // Only allow digits, max 3 characters
        const numericValue = value.replace(/[^0-9]/g, '')

        if (numericValue.length > 3) return

        // Only update local state - no Redux dispatching
        setInputBoxValue(numericValue)
    }

    const HandleLoadCompletedBlur = () => {
        if (disableCardInputs) return

        // If input is empty, just reset focus state (don't update Redux)
        if (inputBoxValue === '') {
            setIsInputBoxCurrentlyFocused(false)
            return
        }

        // Get raw value 
        
        let rawValue = parseInt(inputBoxValue, 10) || 0
        let rawValueClamped = Math.max(0, Math.min(rawValue, 500)); // 500 lb max input

        // Get rounded value

        let roundedValue = Math.round(rawValueClamped / 5) * 5;

        // Calculate override amount

        let overrideAmount = (roundedValue - labelWeightRecommendationRounded) / 5

        __DispatchWeightRecommendationOverride(
            parseFloat(rawValue),
            parseInt(roundedValue),
            parseInt(overrideAmount)
        )

        // Reset local state
        setInputBoxValue('')
        setIsInputBoxCurrentlyFocused(false)
    }

    const __DispatchWeightRecommendationOverride = (weightRecommendation, weightRecommendationRounded, override) => {
        const payload = {
            workoutId: urlWorkoutId,
            kinesisWorkoutAssignmentType: kinesisWorkoutAssignmentType,
            groupIndividualId: groupIndividualId,
            phaseIndex: phaseIndex,
            slotIndex: slotIndex,
            setIndex: setIndex,
            weightRecommendation: weightRecommendation,
            weightRecommendationRounded: weightRecommendationRounded,
            override: override
        }

        dispatch(updateSetWeightRecommendationOverride(payload))
    }

    if (isSkipped === 1) {
        return null
    }

    if (exercise.exerciseType === 'bodyweight') {
        return null
    }

    if (weightRecommendationLoaded === false) {
        return (
            <div className='space-y-2'>
                {showLabel && (
                    <div className='h-4 text-xs text-center text-gray-600'>
                        Load
                    </div>
                )}

                <div className='flex items-center justify-center'>
                    <div className='flex-1 pr-1 py-4'>
                        <ChevronLeftIcon className='ml-auto w-10 h-10 text-gray-400' />
                    </div>

                    <div className="w-20 h-20 bg-gray-200 rounded-xl animate-pulse" />

                    <div className='flex-1 pl-1 py-4'>
                        <ChevronRightIcon className='mr-auto w-10 h-10 text-gray-400' />
                    </div>
                </div>

            </div>
        )
    }

    return (
        <div className='space-y-2'>
            {showLabel && (
                <div className='h-4 text-xs text-center text-gray-600'>
                    Load
                </div>
            )}

            <div className='flex items-center justify-center'>
                {disableCardInputs === false &&
                    <div 
                        className='flex-1 pr-1 py-4' // bg-red-100
                        onClick={() => DecrementLoadCompleted()}
                    >
                        <ChevronLeftIcon className='ml-auto w-10 h-10 text-gray-600' />
                    </div>
                }

                <div 
                    className={`
                        relative 
                        ${isScaling ? 'animate-scale-bounce' : ''}
                    `}
                >
                    <input 
                        type="text" 
                        inputMode="numeric"
                        className='w-20 h-20 rounded-xl flex items-center justify-center text-4xl text-center text-gray-900 pb-3 border border-gray-400 bg-white'
                        value={isInputBoxCurrentlyFocused ? inputBoxValue : String(weightRecommendationRounded)} 
                        disabled={disableCardInputs}
                        onFocus={HandleInputBoxFocus}
                        onChange={(event) => HandleLoadCompletedChange(event.target.value)}
                        onBlur={HandleLoadCompletedBlur}
                    />

                    <div className='absolute bottom-2 left-0 w-full text-center text-xs text-gray-800'>
                        lbs
                    </div>
                </div>

                {disableCardInputs === false &&
                    <div 
                        className='flex-1 pl-1 py-4' // bg-red-100
                        onClick={() => IncrementLoadCompleted()}
                    >
                        <ChevronRightIcon className='mr-auto w-10 h-10 text-gray-600' />
                    </div>
                }
            </div>
        </div>
    )
}